<template>
  <div
    v-if="articleData"
    class="article-modal-shell"
  >
    <button
      type="button"
      class="article-modal-close"
      aria-label="Fermer"
      @click.prevent="closeArticleForm"
    >
      <i class="fa fa-times"></i>
    </button>

    <header class="article-modal-header">
      <h3>{{ articleData.uuid ? 'Modifier l\'article' : 'Nouvel article' }}</h3>
    </header>

    <div class="article-modal-layout">
      <main class="article-modal-main">
        <input
          v-model="articleData.title"
          type="text"
          class="article-title-input"
          placeholder="Titre de l'article"
        />

        <section class="article-editor-panel">
          <quill-editor
            ref="myQuillEditor"
            v-model="articleData.body"
            :options="quillOptions"
          />
        </section>

        <p class="article-link-helper">
          <strong>Pour ajouter un lien vers une ressource :</strong>
          surlignez le mot sur lequel créer un lien, cliquez sur le bouton « Lien » puis renseignez l’URL au format :
          <code>/article/identifiant-unique</code>,
          <code>/question/identifiant-unique</code>,
          <code>/sondage/identifiant-unique</code>,
          <code>/document/identifiant-unique</code>
        </p>

        <section class="article-illustration-panel">
          <div class="article-panel-title">Illustration</div>

          <div
            v-if="thumbnailUrl || articleData.thumbnail"
            class="article-thumbnail-preview"
          >
            <img
              :src="thumbnailUrl || (storageUrl + '/thumbnails/' + articleData.thumbnail)"
              alt="Illustration de l'article"
            />
            <button
              type="button"
              @click.prevent="clearThumbnail"
            >
              Supprimer
            </button>
          </div>

          <div class="article-illustration-actions">
            <file-upload
              extensions="gif,jpg,jpeg,png"
              accept="image/*"
              :maximum="1"
              :multiple="false"
              v-model="thumbnailFiles"
              @input-filter="inputFilter"
              @input-file="inputThumbnail"
              ref="upload"
              input-id="thumbnail"
              class="article-upload-action"
            >
              <span class="article-action-icon"><i class="fa fa-upload"></i></span>
              <span>
                <strong>Importer</strong>
                <small>Depuis votre ordinateur</small>
              </span>
            </file-upload>

            <button
              type="button"
              class="article-upload-action article-upload-action--search"
              :disabled="!articleData.title || isSearchingImageSuggestions"
              @click.prevent="searchImageSuggestions()"
            >
              <span class="article-action-icon"><i class="fa fa-wand-magic-sparkles"></i></span>
              <span>
                <strong>{{ isSearchingImageSuggestions ? 'Recherche...' : 'Rechercher' }}</strong>
                <small>sur une base d'image libre de droit</small>
              </span>
            </button>
          </div>
        </section>
      </main>

      <aside class="article-modal-sidebar">
        <section class="article-sidebar-section">
          <div class="article-panel-title">Documents</div>
          <ul
            v-if="articleData.documents && articleData.documents.length"
            class="article-document-list"
          >
            <li
              v-for="doc in articleData.documents"
              :key="doc.uuid || doc.id"
            >
              <span>{{ doc.name }}</span>
              <button
                type="button"
                @click.prevent="deleteFile(doc)"
              >
                <i class="fa fa-trash"></i>
              </button>
            </li>
          </ul>
          <ul
            v-if="articleDocuments.length"
            class="article-document-list article-document-list--pending"
          >
            <li
              v-for="doc in articleDocuments"
              :key="doc.name"
            >
              <span>{{ doc.name }}</span>
              <button
                type="button"
                @click.prevent="removePendingDocument(doc)"
              >
                <i class="fa fa-trash"></i>
              </button>
            </li>
          </ul>
          <file-upload
            extensions="pdf,doc,docx,xls,xlsx"
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            :multiple="true"
            v-model="documents"
            @input-filter="inputFilter"
            @input-file="inputDocumentFile"
            ref="documentUpload"
            input-id="documents"
            class="article-document-upload"
          >
            <i class="fa fa-paperclip"></i>
            <span>Ajouter des documents</span>
          </file-upload>
        </section>

        <section class="article-sidebar-section">
          <div class="article-panel-title">Date de publication</div>
          <t-datepicker
            timepicker
            v-model="articleData.publication_date"
            date-format="Y-m-d H:i:S"
            user-format="d/m/Y H:i"
            placeholder="Sélectionner..."
            :locale="locale"
            clearable
            required
            :disabled="articleData.is_notified == 1"
            :disabled-dates="articleData.is_notified == 1 ? () => true : null"
          />
        </section>

        <section
          v-if="usersGroups"
          class="article-sidebar-section"
        >
          <div class="article-panel-title">Groupes</div>
          <t-rich-select
            multiple
            :close-on-select="false"
            :options="usersGroups"
            placeholder="Sélectionner..."
            v-model="groupsSelected"
          ></t-rich-select>
        </section>

        <section
          v-if="this.domain.slug === '0-media-interne'"
          class="article-sidebar-section"
        >
          <div class="article-panel-title">Thème</div>
          <t-rich-select
            v-model="articleData.theme"
            :options="themes"
            placeholder="Sélectionner..."
          />
        </section>

        <section
          v-if="this.domain.slug === '0-media-interne'"
          class="article-sidebar-section"
        >
          <div class="article-panel-title">Applications cibles</div>
          <div class="article-radio-group">
            <label>
              <input
                type="radio"
                name="media-mvp-visibility"
                value="both"
                v-model="articleData.media_mvp_visibility"
              />
              <span>Maviepro &amp; Tract</span>
            </label>
            <label>
              <input
                type="radio"
                name="media-mvp-visibility"
                value="maviepro"
                v-model="articleData.media_mvp_visibility"
              />
              <span>Maviepro</span>
            </label>
            <label>
              <input
                type="radio"
                name="media-mvp-visibility"
                value="tract"
                v-model="articleData.media_mvp_visibility"
              />
              <span>Tract</span>
            </label>
          </div>
        </section>
      </aside>
    </div>

    <footer class="article-modal-footer">
      <div class="article-footer-options">
        <label class="article-status-toggle">
          <t-toggle
            v-model="articleData.is_enabled"
            :value="true"
            :uncheckedValue="false"
          />
          <span>{{ articleData.is_enabled ? 'Actif' : 'Inactif' }}</span>
        </label>

        <label
          v-if="domain.can_avoid_notifications && !articleData.uuid"
          class="article-status-toggle"
        >
          <t-toggle
            v-model="articleData.to_notify"
            :value="false"
            :uncheckedValue="true"
          />
          <span>Ne pas notifier</span>
        </label>

        <label
          v-if="shouldDisplayShareToggle"
          class="article-status-toggle"
        >
          <t-toggle
            v-model="articleData.is_shareable"
            :value="true"
            :uncheckedValue="false"
          />
          <span>Partage public</span>
        </label>
      </div>

      <div class="article-footer-actions">
        <button
          v-if="articleData.uuid && canRenotify"
          type="button"
          class="article-secondary-button"
          @click.prevent="notifyArticle"
        >
          Renotifier
        </button>
        <button
          v-if="articleData.uuid && hasDuplicationRights"
          type="button"
          class="article-secondary-button"
          :disabled="duplicatingArticle"
          @click.prevent="duplicateArticle"
        >
          {{ duplicatingArticle ? 'Duplication...' : 'Dupliquer' }}
        </button>
        <button
          type="button"
          class="article-save-button"
          @click.prevent="saveArticle"
        >
          Sauvegarder
        </button>
      </div>
    </footer>

    <div
      v-if="imageSuggestions.length"
      class="article-suggestions-overlay"
      @click.self="closeImageSuggestions"
    >
      <div class="article-suggestions-card">
        <div class="article-suggestions-header">
          <div>
            <div class="article-panel-title">Choisir une illustration</div>
          </div>
          <div class="article-suggestions-header-actions">
            <button
              type="button"
              class="article-suggestions-refresh"
              title="Afficher d'autres propositions"
              aria-label="Afficher d'autres propositions"
              :disabled="isSearchingImageSuggestions || isImportingImageSuggestion"
              @click.prevent="refreshImageSuggestions"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': isSearchingImageSuggestions }"></i>
            </button>
            <button
              type="button"
              class="article-suggestions-close"
              aria-label="Fermer les suggestions"
              @click.prevent="closeImageSuggestions"
            >
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <div class="image-suggestions">
          <button
            v-for="suggestion in imageSuggestions"
            :key="suggestion.provider + '-' + suggestion.id"
            type="button"
            class="image-suggestion"
            :class="{ 'image-suggestion--selected': selectedImageSuggestionKey === getImageSuggestionKey(suggestion) }"
            @click="selectImageSuggestion(suggestion)"
          >
            <img
              :src="suggestion.thumbnail_url"
              :alt="suggestion.title"
            />
          </button>
          <div class="image-suggestions__footer">
            <button
              type="button"
              class="article-secondary-button"
              @click.prevent="closeImageSuggestions"
            >
              Annuler
            </button>
            <button
              type="button"
              class="article-save-button"
              :disabled="!selectedImageSuggestion || isImportingImageSuggestion"
              @click.prevent="validateImageSuggestion"
            >
              {{ isImportingImageSuggestion ? 'Import...' : 'Valider le visuel' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showCropper"
      class="article-cropper-overlay"
      @click.self="cancelCrop"
    >
      <div class="article-cropper-card">
        <div class="article-cropper-image">
          <img
            ref="imageToCrop"
            :src="imageSrc"
            class="block mx-auto object-contain"
          />
        </div>
        <div class="article-cropper-actions">
          <button
            type="button"
            class="article-secondary-button"
            @click="cancelCrop"
          >
            Annuler
          </button>
          <button
            type="button"
            class="article-save-button"
            @click="applyCrop"
          >
            Insérer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import VueTagsInput from '@johmun/vue-tags-input'
import { quillEditor } from 'vue-quill-editor'

import FileUpload from 'vue-upload-component'

import { http } from '@/services/api.service'
import { format } from 'date-fns'
import French from 'vue-tailwind/dist/l10n/fr'

import PageHeader from '@/components/PageHeader'
import PageContent from '@/components/PageContent.vue'
import faqAccordionQuill from '@/mixins/faqAccordionQuill'
import { normalizeInternalContentLinks } from '@/utils/internalContentLinks'

import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { mapGetters } from 'vuex'


export default {
  name: 'mvp-articles-form',
  components: {
    PageHeader,
    PageContent,
    quillEditor,
    FileUpload,
    VueTagsInput
  },
  mixins: [faqAccordionQuill],
  computed: {
    ...mapGetters([
      'domain',
      'roles'
    ]),
    hasDuplicationRights () {
      return this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    isTractApp () {
      return process.env.VUE_APP_APPLICATION === 'tract'
    },
    shouldDisplayShareToggle () {
      return this.isTractApp
    },
    selectedImageSuggestion () {
      return this.imageSuggestions.find(suggestion => this.getImageSuggestionKey(suggestion) === this.selectedImageSuggestionKey) || null
    }
  },
  watch: {
    selectedDocumentUuid (uuid) {
      const doc = (this.documentsList || []).find(d => d.uuid === uuid)
      const files = (doc && Array.isArray(doc.files)) ? doc.files : []
      this.documentFilesOptions = files.map(f => ({ value: f.uuid, text: f.name || f.uuid }))
      this.selectedFileUuid = files.length === 1 ? files[0].uuid : null
    }
  },
  data () {
    return {
      upload: null,
      showCropper: false,
      imageSrc: '',
      cropper: null,
      isEditorImage: false,
      themes: [
        { value: 'Juridique', text: 'Juridique' },
        { value: 'Société', text: 'Société' },
        { value: 'Dialogue social', text: 'Dialogue social' },
        { value: 'Économie', text: 'Économie' }
      ],
      articleData: {
        publication_date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        groups: [],
        theme: null,
        media_mvp_visibility: 'both',
        to_notify: true,
        is_enabled: false,
        is_shareable: false,
      },
      articleFiles: [],
      articleUniqueKey: null,
      currentKeyword: '',
      articleKeywords: [],
      thumbnailFile: null,
      thumbnailUrl: '',
      document_file: null,
      thumbnail: null,
      thumbnailSource: null,
      thumbnailFiles: [],
      storageUrl: process.env.VUE_APP_STORAGE_URL,
      filesToAdd: [],
      groupsSelected: [],
      locale: French,
      documents: [],
      articleDocuments: [],
      usersGroups: [],
      documentsList: [],
      documentsOptions: [],
      documentFilesOptions: [],
      selectedDocumentUuid: null,
      selectedFileUuid: null,
      imageSuggestions: [],
      selectedImageSuggestionKey: null,
      imageSuggestionQuery: null,
      excludedImageSuggestionIds: [],
      isSearchingImageSuggestions: false,
      isImportingImageSuggestion: false,
      isClearingThumbnail: false,
      duplicatingArticle: false
    }
  },
  created () {
    this.extendArticleQuillToolbar()
  },
  props: {
    article: {
      type: Object,
      default: null
    },
    articleKey: {
      type: [String, Number],
      default: null
    },
    groups: {
      type: Array,
      default: () => []
    },
    stayOnDashboard: {
      type: Boolean,
      default: false
    },
    canRenotify: {
      type: Boolean,
      default: true
    }
  },
  beforeMount: async function () {
    this.articleUniqueKey = typeof this.$route.params.uuid !== 'undefined'
      ? this.$route.params.uuid : this.articleKey

    this.groups.forEach(group => {
      this.usersGroups.push({
        value: group.uuid,
        text: group.label
      })
    })
    try {
      const { data } = await http.get('/events')
      this.documentsList = Array.isArray(data) ? data : []
      this.documentsOptions = this.documentsList.map(ev => ({ value: ev.uuid, text: ev.title }))
    } catch (e) {
      this.documentsList = []
      this.documentsOptions = []
    }
    // article loading
    if (typeof this.articleUniqueKey !== 'undefined') {
      if (!this.articleUniqueKey) {
        return
      }
      await http.get(`/article/${this.articleUniqueKey}`)
        .then(({ data }) => {
          this.articleData = data
          this.parseArticle()
        })
        .catch(err => {
          console.log(err)
          if (err && err.response && err.response.status === 404) {
            this.$notify({
              group: 'maviepro-error',
              text: 'Article introuvable.'
            })
          } else {
            this.$notify({
              group: 'maviepro-error',
              text: 'Impossible de récupérer l\'article.'
            })
          }
          this.closeArticleForm()
        })
    }

  },
  methods: {
    closeArticleForm () {
      if (this.stayOnDashboard === true || this.stayOnDashboard === 'true') {
        this.$emit('close')
        return
      }
      if (this.$parent && typeof this.$parent.hide === 'function') {
        this.$parent.hide()
        return
      }
      if (this.$parent && this.$parent.$modal && this.$parent.$modal.hide) {
        this.$parent.$modal.hide('article-form')
        return
      }
      if (this.$parent && this.$parent.close) {
        this.$parent.close()
      }
    },
    applyYoutubeEmbedPolicy(body) {
      if (!body || typeof document === 'undefined') {
        return body
      }
      const container = document.createElement('div')
      container.innerHTML = body
      const iframes = container.querySelectorAll('iframe')
      const requiredPermissions = ['accelerometer', 'autoplay', 'clipboard-write', 'encrypted-media', 'gyroscope', 'picture-in-picture', 'web-share']
      const canonicalOrigin = 'https://www.youtube.com'

      iframes.forEach((iframe) => {
        const src = (iframe.getAttribute('src') || '').toLowerCase()
        if (!src || (src.indexOf('youtube.com') === -1 && src.indexOf('youtu.be') === -1)) {
          return
        }
        iframe.setAttribute('src', this.enforceYoutubeIdentity(iframe.getAttribute('src'), canonicalOrigin))
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin')
        iframe.setAttribute('allowfullscreen', 'allowfullscreen')
        if (!iframe.getAttribute('title')) {
          iframe.setAttribute('title', 'YouTube video player')
        }

        const currentPermissions = (iframe.getAttribute('allow') || '')
          .split(';')
          .map(permission => permission.trim())
          .filter(Boolean)
        const mergedPermissions = new Set(currentPermissions)
        requiredPermissions.forEach(permission => mergedPermissions.add(permission))
        iframe.setAttribute('allow', Array.from(mergedPermissions).join('; '))
      })

      return container.innerHTML
    },
    enforceYoutubeIdentity(src, canonicalOrigin) {
      if (!src) {
        return src
      }
      let url
      try {
        url = new URL(src, window.location.origin)
      } catch (e) {
        return src
      }
      const host = url.hostname.replace(/^www\./, '')
      const searchParams = url.searchParams
      let embedPath = url.pathname

      if (host === 'youtu.be') {
        const videoId = url.pathname.slice(1)
        if (videoId) {
          embedPath = `/embed/${videoId}`
        }
      }

      if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtu.be') {
        if (url.pathname === '/watch') {
          const videoId = searchParams.get('v')
          if (videoId) {
            embedPath = `/embed/${videoId}`
          }
        }
      }

      searchParams.set('origin', canonicalOrigin)
      searchParams.set('widget_referrer', canonicalOrigin)

      const baseUrl = `https://www.youtube.com${embedPath}`
      const query = searchParams.toString()
      return query ? `${baseUrl}?${query}` : baseUrl
    },
    extendArticleQuillToolbar () {
      if (!this.quillOptions || !this.quillOptions.modules || !this.quillOptions.modules.toolbar) return
      const baseToolbar = this.quillOptions.modules.toolbar
      const baseHandlers = baseToolbar.handlers ? { ...baseToolbar.handlers } : {}
      this.quillOptions = {
        ...this.quillOptions,
        bounds: '.article-editor-panel',
        placeholder: 'Rédigez votre article ici...',
        modules: {
          ...this.quillOptions.modules,
          toolbar: {
            ...baseToolbar,
            container: [
              ['bold', 'italic'],
              [{ list: 'bullet' }],
              ['link', 'image', 'video'],
              ['accordion'],
              ['table', 'table-row', 'table-column', 'table-row-remove', 'table-column-remove']
            ],
            handlers: {
              ...baseHandlers,
              image: this.handleImageInsertion
            }
          }
        }
      }
    },
    normalizeMediaMvpVisibility (value) {
      const allowed = ['both', 'maviepro', 'tract']
      return allowed.includes(value) ? value : 'both'
    },
    parseArticle() {
      this.articleData.media_mvp_visibility = this.normalizeMediaMvpVisibility(this.articleData.media_mvp_visibility)
      this.articleKeywords = []
      if (this.articleData.keywords) {
        this.articleData.keywords.forEach(keyword => {
          this.articleKeywords.push({
            text: keyword
          })
        })
      }

      if (this.articleData.notifications) {
        this.articleData.notifications.forEach(notification => {
          if (notification.group && notification.group.uuid) {
            this.groupsSelected.push(notification.group.uuid)
          }
        })
      }

      if (this.articleData.files) {
        this.articleData.files.forEach(file => {
          this.articleFiles.push({
            source: file.url,
            options: {
              type: 'local',
              file: {
                name: file.name,
                size: file.size
              },
              metadata: {
                uuid: file.uuid
              }
            }
          })
        })
      }
    },

    parser(date) {
      return parse(date)
    },
    getImgUrl() {
      return `${this.storageUrl}/thumbnails/${this.articleData.thumbnail}`
    },
    isDisabledValue(value) {
      return value === false || value === 0 || value === '0' || value === 'false'
    },
    saveArticle() {
      const formData = new FormData()
      const isUpdate = !!this.articleData.uuid
      console.log('isUpdate', isUpdate)
      const { thumbnail_file, ...data } = this.articleData
      data.body = normalizeInternalContentLinks(this.applyYoutubeEmbedPolicy(data.body))
      data.media_mvp_visibility = this.normalizeMediaMvpVisibility(data.media_mvp_visibility)
      if (this.isDisabledValue(data.is_enabled)) {
        data.is_pinned = 0
        data.pinned_position = null
        this.articleData.is_pinned = 0
        this.articleData.pinned_position = null
      }
      const postUrl = '/article' + (isUpdate ? `/${this.articleData.uuid}` : '')
      const currentStatus = this.$route.query.status
      for (const prop in data) {
        formData.append(prop, data[prop])
      }

      formData.delete('is_shareable')
      formData.append('is_shareable', this.articleData.is_shareable ? 1 : 0)

      // Append keywords
      formData.delete('keywords')
      if (this.articleData.keywords && this.articleData.keywords.length) {
        this.articleData.keywords.forEach(keyword => {
          formData.append('keywords[]', keyword)
        })
      }

      // Append group UUIDs
      this.groupsSelected.forEach(groupUuid => {
        formData.append('groupUuids[]', groupUuid)
      })

      // Handling files and thumbnail for new article
      if (!isUpdate) {
        if (this.thumbnail) {
          formData.append('thumbnail', this.thumbnail)
          this.appendThumbnailSource(formData)
        }
        this.filesToAdd.forEach(file => {
          formData.append('files[]', file)
        })
      }

      http.post(postUrl, formData).then(res => {
        const stayOnDashboard = this.stayOnDashboard === true || this.stayOnDashboard === 'true'
        const savedArticle = res?.data || null
        const statusSource = savedArticle && Object.prototype.hasOwnProperty.call(savedArticle, 'is_enabled')
          ? savedArticle
          : this.articleData
        const nextStatus = Object.prototype.hasOwnProperty.call(statusSource, 'is_enabled')
          ? (this.isDisabledValue(statusSource.is_enabled) ? 'inactive' : 'active')
          : (currentStatus || 'active')

        this.$emit('refresh', savedArticle, nextStatus, {
          action: 'save',
          isEnabled: nextStatus === 'active',
          isNew: !isUpdate
        })
        this.$parent.$modal.hide('article-form')
        this.$notify({
          group: 'maviepro-success',
          text: 'Article mis à jour.'
        })
        if (!stayOnDashboard) {
          this.$router.replace({
            name: 'app-articles',
            query: { status: nextStatus, refreshToken: Date.now() }
          }).catch(() => {})
        }
      }).catch(err => {
        console.log(err)
        const message = this.getErrorMessage(err, 'Erreur lors de la sauvegarde de l\'article.')
        this.$notify({
          group: 'maviepro-error',
          text: message
        })
      })
    },
    deleteArticle() {
      this.$dialog.confirm({
        title: 'Supprimer cet article ?',
        text: 'Cette action ne peut pas être annulée.',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        okButtonText: 'Supprimer'
      }).then((result) => {
        if (result.isOk && result.hideReason === 'ok') {
          http.delete(`/article/${this.articleUniqueKey}`).then(res => {
            this.$notify({
              group: 'maviepro-success',
              text: 'Article supprimé.'
            })
            this.$emit('refresh')
            this.$parent.$modal.hide('article-form')
          }).catch(err => {
            console.log(err)
            this.$notify({
              group: 'maviepro-error',
              text: 'Problème lors de la suppression, veuillez contacter un administrateur.'
            })
          })
        }
      })
    },
    notifyArticle() {
      this.$dialog.confirm({
        title: 'Notification',
        text: 'Êtes vous sûr de vouloir renotifier cet article ?',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        okButtonText: 'Renotifier'
      }).then((result) => {
        if (result.isOk && result.hideReason === 'ok') {
          http.post(`/article/${this.articleData.uuid}/notify`).then(res => {
            this.$notify({
              group: 'maviepro-success',
              text: 'Notification envoyée.'
            })
            this.$emit('refresh', res?.data || this.articleData)
            this.$parent.$modal.hide('article-form')
          }).catch(err => {
            console.log(err)
            this.$notify({
              group: 'maviepro-error',
              text: 'Erreur lors de l\'envoi de la notification.'
            })
          })
        }
      });
    },
    duplicateArticle () {
      if (!this.articleData.uuid) {
        this.$notify({
          group: 'maviepro-error',
          text: 'Impossible de dupliquer un article non sauvegardé.'
        })
        return
      }
      if (this.duplicatingArticle) {
        return
      }

      this.$dialog.confirm({
        title: 'Duplication de l\'article',
        text: 'Voulez-vous vraiment dupliquer cet article ? En confirmant, une copie inactive sera créée dans les brouillons.',
        icon: 'warning',
        cancelButtonText: 'Annuler',
        okButtonText: 'Dupliquer'
      }).then((result) => {
        if (result.isOk && result.hideReason === 'ok') {
          this.duplicatingArticle = true
          const duplicationData = {
            domain: this.domain
          }
          http.post(`/article/${this.articleData.uuid}/duplicate`, duplicationData)
            .then(res => {
              this.duplicatingArticle = false
              const stayOnDashboard = this.stayOnDashboard === true || this.stayOnDashboard === 'true'
              this.$notify({
                group: 'maviepro-success',
                text: 'Article dupliqué avec succès.'
              })
              this.$emit('refresh', res?.data || null, 'drafts', {
                action: 'duplicate',
                isEnabled: false
              })
              this.closeArticleForm()
              if (!stayOnDashboard) {
                this.$router.replace({
                  name: 'app-contents',
                  query: {
                    tab: 'drafts',
                    refreshToken: Date.now().toString()
                  }
                }).catch(() => {})
              }
            })
            .catch(err => {
              this.duplicatingArticle = false
              console.log(err)
              this.$notify({
                group: 'maviepro-error',
                text: 'Erreur lors de la duplication de l\'article.'
              })
            })
        }
      })
    },
    updateTags(tags) {
      this.articleData.keywords = tags.map(tag => tag.text)
    },

    getImageSuggestionKey (suggestion) {
      return suggestion ? `${suggestion.provider}:${suggestion.id}` : null
    },
    selectImageSuggestion (suggestion) {
      this.selectedImageSuggestionKey = this.getImageSuggestionKey(suggestion)
    },
    closeImageSuggestions () {
      if (this.isImportingImageSuggestion) {
        return
      }
      this.imageSuggestions = []
      this.selectedImageSuggestionKey = null
      this.imageSuggestionQuery = null
      this.excludedImageSuggestionIds = []
    },
    refreshImageSuggestions () {
      if (!this.isSearchingImageSuggestions && !this.isImportingImageSuggestion) {
        this.searchImageSuggestions(true)
      }
    },
    searchImageSuggestions (refresh = false) {
      if (!this.articleData.title) {
        this.$notify({
          group: 'maviepro-error',
          text: 'Veuillez renseigner le titre avant de rechercher une illustration.'
        })
        return
      }

      this.isSearchingImageSuggestions = true
      if (!refresh) {
        this.imageSuggestions = []
        this.selectedImageSuggestionKey = null
        this.imageSuggestionQuery = null
        this.excludedImageSuggestionIds = []
      }

      http.post('/articles/image-suggestions', {
        title: this.articleData.title,
        body: this.articleData.body || '',
        query: refresh ? this.imageSuggestionQuery : null,
        excluded_ids: refresh ? this.excludedImageSuggestionIds : []
      }).then(({ data }) => {
        const payload = data && data.data ? data.data : data
        this.imageSuggestions = Array.isArray(payload.suggestions) ? payload.suggestions : []
        this.imageSuggestionQuery = payload.query || this.imageSuggestionQuery
        this.excludedImageSuggestionIds = [
          ...new Set([
            ...this.excludedImageSuggestionIds,
            ...this.imageSuggestions.map(suggestion => String(suggestion.id))
          ])
        ]
        if (this.imageSuggestions.length) {
          this.selectedImageSuggestionKey = this.getImageSuggestionKey(this.imageSuggestions[0])
        }
      }).catch(err => {
        console.log(err)
        this.$notify({
          group: 'maviepro-error',
          text: this.getErrorMessage(err, 'Aucun visuel libre de droit n\'a été trouvé.')
        })
      }).finally(() => {
        this.isSearchingImageSuggestions = false
      })
    },
    validateImageSuggestion () {
      const suggestion = this.selectedImageSuggestion
      if (!suggestion) {
        return
      }

      this.isImportingImageSuggestion = true
      http.post('/articles/image-suggestions/download', {
        provider: suggestion.provider,
        id: suggestion.id,
        url: suggestion.download_url
      }, {
        responseType: 'blob'
      }).then(({ data }) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.thumbnailSource = {
            provider: suggestion.provider,
            id: suggestion.id
          }
          this.prepareThumbnailCropFromDataUrl(e.target.result)
          this.imageSuggestions = []
          this.selectedImageSuggestionKey = null
          this.imageSuggestionQuery = null
          this.excludedImageSuggestionIds = []
        }
        reader.readAsDataURL(data)
      }).catch(err => {
        console.log(err)
        this.$notify({
          group: 'maviepro-error',
          text: this.getErrorMessage(err, 'Impossible d\'importer ce visuel.')
        })
      }).finally(() => {
        this.isImportingImageSuggestion = false
      })
    },
    prepareThumbnailCropFromDataUrl (dataUrl) {
      this.isEditorImage = false
      const img = new Image()
      img.onload = () => {
        if (img.naturalWidth < 595 || img.naturalHeight < 350) {
          this.thumbnailSource = null
          this.$notify({
            group: 'maviepro-error',
            text: 'L\'image est trop petite. Veuillez sélectionner une image d\'au moins 595px de large et 350px de haut.'
          })
          return
        }
        this.imageSrc = dataUrl
        this.showCropper = true
        this.$nextTick(() => {
          const image = this.$refs.imageToCrop
          this.cropper = new Cropper(image, {
            aspectRatio: 595 / 350,
            viewMode: 3,
            dragMode: 'crop',
            autoCropArea: 1,
            background: false,
            cropBoxMovable: true,
            cropBoxResizable: true,
            responsive: true,
            zoomable: false,
            cropend: () => {
              const data = this.cropper.getData();
              const newWidth  = Math.max(595, Math.round(data.width))
              const newHeight = Math.max(350, Math.round(data.height))

              if (newWidth !== Math.round(data.width) || newHeight !== Math.round(data.height)) {
                this.cropper.setData({
                  width: newWidth,
                  height: newHeight
                })
              }
            }
          })
        })
      }
      img.src = dataUrl
    },

    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent()
        }

        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent()
        }
      }
    },
    inputThumbnail (newFile, oldFile) {
      this.isEditorImage = false
      if (newFile && !oldFile) {
        this.thumbnailSource = null
        let reader = new FileReader()
        reader.readAsDataURL(newFile.file)
        reader.onload = (e) => {
          this.prepareThumbnailCropFromDataUrl(e.target.result)
        }
      }
      if (!newFile && oldFile) {
        this.thumbnailUrl = ''
        this.thumbnailSource = null
        if (this.isClearingThumbnail) {
          return false
        }
        if (!this.articleData.uuid) {
          return false
        }
        http.delete(`/article/${this.articleData.uuid}/thumbnail`).then(res => {
          this.thumbnailUrl = null
          this.thumbnail = null
          this.articleData.thumbnail = null
          this.articleData.thumbnail_extension = null
        }).catch(err => {
          console.log(err)
          this.$notify({
            group: 'maviepro-error',
            text: 'Problème lors de la suppression de la miniature.'
          })
        })
      }
    },


    addThumbnail(error, file) {
      if (file && file.file) {
        this.thumbnail = file.file
      }
      // creation, fill only
      if (!this.articleData.uuid) {
        return false
      }
      if (error) return false
      // update: upload file now
      const formData = new FormData()
      if (file) {
        formData.append('thumbnail', this.thumbnail)
        this.appendThumbnailSource(formData)
      }
      http.post(`/article/${this.articleData.uuid}/thumbnail`, formData).then(({ data }) => {
        this.articleData.thumbnail = data
      }).catch(err => {
        console.log(err)
      })
    },
    appendThumbnailSource (formData) {
      if (!this.thumbnailSource || !this.thumbnailSource.provider || !this.thumbnailSource.id) {
        return
      }

      formData.append('thumbnail_source_provider', this.thumbnailSource.provider)
      formData.append('thumbnail_source_id', this.thumbnailSource.id)
    },
    clearThumbnail () {
      this.thumbnailUrl = ''
      this.thumbnail = null
      this.thumbnailSource = null
      this.isClearingThumbnail = true
      this.thumbnailFiles = []
      this.$nextTick(() => {
        this.isClearingThumbnail = false
      })

      if (!this.articleData.uuid || !this.articleData.thumbnail) {
        this.articleData.thumbnail = null
        return
      }

      http.delete(`/article/${this.articleData.uuid}/thumbnail`).then(() => {
        this.articleData.thumbnail = null
      }).catch(err => {
        console.log(err)
        this.$notify({
          group: 'maviepro-error',
          text: 'Problème lors de la suppression de la miniature.'
        })
      })
    },
    // Documents part
    inputDocumentFile(newDoc, oldDoc) {
      if (newDoc && !oldDoc) {
        this.addFile(false, newDoc)
        if (this.articleData.uuid) {
          return
        }

        let reader = new FileReader()
        reader.readAsDataURL(newDoc.file)
        reader.onload = (e) => {
          this.articleDocuments.push({
            name: newDoc.name,
            uploadId: newDoc.id,
            data: e.target.result
          })
        }
      }
      if (!newDoc && oldDoc) {
        let index = this.articleDocuments.findIndex(d => d.name === oldDoc.name)
        if (index !== -1) this.articleDocuments.splice(index, 1)
        this.removePendingDocumentFile(oldDoc)
      }
    },
    removePendingDocument(doc) {
      const uploadFile = this.documents.find(file => file.id === doc.uploadId || file.name === doc.name)

      if (uploadFile && this.$refs.documentUpload) {
        this.$refs.documentUpload.remove(uploadFile)
        return
      }

      const index = this.articleDocuments.findIndex(document => document.name === doc.name)
      if (index !== -1) {
        this.articleDocuments.splice(index, 1)
      }
    },
    addFile(error, file) {
      // creation, fill only
      if (!this.articleData.uuid) {
        if (file && file.file) {
          this.filesToAdd.push(file.file)
        }
        return false
      }
      if (error) return false
      // update: upload file now
      const formData = new FormData()
      if (file && file.file) {
        formData.append('files[]', file.file)
      }
      http.post(`/article/${this.articleData.uuid}/file`, formData).then(({ data }) => {
        this.articleData.files = [...(this.articleData.files || []), ...data]
        this.articleData.documents = [...(this.articleData.documents || []), ...data]
      }).catch(err => {
        console.log(err)
        this.$notify({
          group: 'maviepro-error',
          text: this.getErrorMessage(err, 'Erreur lors de l\'ajout du document.')
        })
      })
    },
    removePendingDocumentFile(uploadFile) {
      if (!uploadFile) {
        return
      }
      const nativeFile = uploadFile.file || null
      const index = this.filesToAdd.findIndex(file => {
        if (nativeFile && file === nativeFile) {
          return true
        }
        const name = uploadFile.name || (nativeFile && nativeFile.name)
        const size = uploadFile.size || (nativeFile && nativeFile.size)
        const lastModified = nativeFile && nativeFile.lastModified
        return file.name === name &&
          file.size === size &&
          (!lastModified || file.lastModified === lastModified)
      })
      if (index !== -1) {
        this.filesToAdd.splice(index, 1)
      }
    },
    getErrorMessage(err, fallback) {
      const data = err && err.response ? err.response.data : null
      if (!data) {
        return fallback
      }
      if (typeof data.message === 'string') {
        return data.message
      }
      const errors = data.errors || (data.message && data.message.errors)
      if (errors && typeof errors === 'object') {
        const firstKey = Object.keys(errors)[0]
        const firstError = firstKey ? errors[firstKey] : null
        if (Array.isArray(firstError) && firstError.length) {
          return firstError[0]
        }
        if (typeof firstError === 'string') {
          return firstError
        }
      }
      return fallback
    },
    deleteFile(file) {
      if (!this.articleData.uuid) {
        return false
      }
      const fileUuid = file['uuid']
      http
        .delete(`/article/${this.articleData.uuid}/file/${fileUuid}`)
        .then(({ data }) => {
          this.articleData.documents = this.articleData.documents.filter(doc => doc.uuid !== fileUuid)
        }).catch(err => {
          console.log(err)
        })
    },

    handleImageInsertion () {
      this.isEditorImage = true
      const fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.accept = 'image/*'
      fileInput.onchange = (e) => {
        const file = e.target.files[0]
        if (file) {
          const reader = new FileReader()
          reader.onload = (event) => {
            this.imageSrc = event.target.result
            this.showCropper = true
            this.$nextTick(() => {
              const image = this.$refs.imageToCrop
              this.cropper = new Cropper(image, {
                viewMode: 2,
                dragMode: 'crop',
                autoCropArea: 1,
                background: false,
                cropBoxMovable: true,
                cropBoxResizable: true,
                responsive: true
              })
            })
          }
          reader.readAsDataURL(file)
        }
      }
      fileInput.click()
    },
    async applyCrop () {
      const canvasOptions = this.isEditorImage
        ? {}
        : {
          width: 595,
          height: 350,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        }
      const canvas = this.cropper.getCroppedCanvas(canvasOptions)
      this.cropper.destroy()
      this.showCropper = false

      if (this.isEditorImage) {
        canvas.toBlob(async (blob) => {
          if (!blob) return
          try {
            const formData = new FormData()
            formData.append('image', blob, 'editor-image.png')
            const { data } = await http.post('/articles/inline-image', formData)
            const url = data && data.url ? data.url : null
            if (url) {
              this.insertImageToEditor(url)
            }
          } catch (e) {
            console.error(e)
          }
        }, 'image/png')
      } else {
        canvas.toBlob((blob) => {
          if (!blob) return
          const croppedFile = new File([blob], 'cropped_thumbnail.png', { type: 'image/png' })
          this.thumbnailUrl = canvas.toDataURL('image/png')
          this.thumbnail = croppedFile
          this.addThumbnail(false, { file: croppedFile })
        }, 'image/png')
      }
    },
    cancelCrop () {
      if (this.cropper) {
        this.cropper.destroy()
      }
      this.showCropper = false
      if (this.isEditorImage) {
      } else {
        this.thumbnailUrl = ''
        this.thumbnailSource = null
      }
    },
    insertImageToEditor (imageUrl) {
      const quillEditor = this.$refs.myQuillEditor.quill
      const range = quillEditor.getSelection()
      quillEditor.insertEmbed(range.index, 'image', imageUrl)
    },
    insertDocumentLink () {
      if (!this.selectedFileUuid) return
      const doc = this.documentsList.find(d => d.uuid === this.selectedDocumentUuid)
      const file = doc && Array.isArray(doc.files) ? doc.files.find(f => f.uuid === this.selectedFileUuid) : null
      if (!file || !file.url) return

      const quillEditor = this.$refs.myQuillEditor && (this.$refs.myQuillEditor.quill || (this.$refs.myQuillEditor.getEditor && this.$refs.myQuillEditor.getEditor()))
      if (!quillEditor) return

      const range = quillEditor.getSelection(true) || { index: quillEditor.getLength(), length: 0 }
      const label = (doc && doc.title) ? doc.title : (file.name || 'Document')
      quillEditor.insertText(range.index, label, { link: file.url })
      quillEditor.setSelection(range.index + String(label).length, 0)
    }
  }
}
</script>

<style
  lang="scss"
  scoped
>
.article-modal-shell {
  position: relative;
  width: 100%;
  height: auto;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1.75rem 1.25rem;
  overflow: hidden;
  color: #161616;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  box-shadow: 0 22px 54px rgba(17, 24, 39, 0.22);
}

.article-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  color: #9ca3af;
  background: transparent;
  border: 0;
  border-radius: 999px;
  font-size: 1.35rem;
  line-height: 1;
}

.article-modal-close:hover {
  background: #f3f4f6;
  color: #111827;
}

.article-modal-header h3 {
  margin: 0 0 1.25rem;
  font-size: 1.55rem;
  font-weight: 700;
  line-height: 1.2;
  color: #121212;
}

.article-modal-layout {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 1.5rem;
  align-items: stretch;
  min-height: 0;
  padding-right: 0.35rem;
  overflow-x: hidden;
  overflow-y: auto;
}

.article-modal-main {
  min-width: 0;
}

.article-title-input {
  display: block;
  width: 100%;
  height: 3.25rem;
  padding: 0 1rem;
  color: #6b7280;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  outline: none;
}

.article-title-input::placeholder {
  color: #c7ccd3;
  font-size: 1rem;
}

.article-title-input:focus {
  border-color: #445877;
  box-shadow: 0 0 0 3px rgba(68, 88, 119, 0.14);
}

.article-editor-panel {
  height: clamp(290px, 42vh, 430px);
  margin-top: 0.9rem;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
}

.article-editor-panel ::v-deep .quill-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.article-editor-panel ::v-deep .ql-toolbar.ql-snow {
  flex: 0 0 auto;
  padding: 0.9rem 1rem;
  border: 0;
  border-bottom: 1px solid #dbe1ea;
}

.article-editor-panel ::v-deep .ql-container.ql-snow {
  flex: 1 1 auto;
  min-height: 0;
  border: 0;
  color: #6b7280;
  font-size: 1rem;
}

.article-editor-panel ::v-deep .ql-editor {
  min-height: 0;
  height: 100%;
  padding: 1.4rem 1rem;
}

.article-editor-panel ::v-deep .ql-editor.ql-blank::before {
  color: #c7ccd3;
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
}

.article-editor-panel ::v-deep .ql-tooltip {
  z-index: 5;
  max-width: calc(100% - 1rem);
  white-space: normal;
}

.article-editor-panel ::v-deep .ql-tooltip input[type='text'] {
  max-width: calc(100% - 7rem);
}

.article-link-helper {
  margin: 0.6rem 0 1rem;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.35;
}

.article-link-helper strong {
  color: #374151;
  font-weight: 700;
}

.article-link-helper code {
  padding: 0.05rem 0.25rem;
  color: #445877;
  background: #eef2f7;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-style: normal;
  white-space: nowrap;
}

.article-illustration-panel,
.article-modal-sidebar {
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
}

.article-illustration-panel {
  padding: 1.1rem 1.5rem 1.4rem;
}

.article-panel-title {
  margin-bottom: 1.35rem;
  color: #222;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-transform: uppercase;
}

.article-thumbnail-preview {
  display: grid;
  grid-template-columns: 160px auto;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}

.article-thumbnail-preview img {
  display: block;
  width: 160px;
  aspect-ratio: 595 / 350;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.article-thumbnail-preview button,
.article-document-list button {
  color: #dc2626;
  background: transparent;
  border: 0;
}

.article-illustration-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.article-upload-action {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 3.4rem;
  padding: 0.75rem;
  color: #1f2937;
  text-align: left;
  background: #fbfcfe;
  border: 1px dashed #cbd5e1;
  border-radius: 0.55rem;
  cursor: pointer;
}

.article-upload-action--search {
  border-color: #9fb0ca;
}

.article-upload-action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.article-action-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  margin-right: 0.65rem;
  color: #445877;
  background: #eef2f7;
  border-radius: 0.5rem;
}

.article-upload-action strong {
  display: block;
  font-size: 0.82rem;
  line-height: 1.1;
}

.article-upload-action small {
  display: block;
  margin-top: 0.2rem;
  overflow-wrap: anywhere;
  color: #9ca3af;
  font-size: 0.68rem;
  line-height: 1.15;
}

.article-modal-sidebar {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 1.5rem;
}

.article-sidebar-section + .article-sidebar-section {
  margin-top: 2rem;
}

.article-document-upload {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 2.35rem;
  width: 100%;
  padding: 0.55rem 0.7rem;
  color: #6b7280;
  background: #fbfcfe;
  border: 1px dashed #cbd5e1;
  border-radius: 0.55rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
}

.article-document-upload i {
  margin-right: 0.5rem;
}

.article-document-list {
  margin: 0 0 0.85rem;
  padding: 0;
  list-style: none;
}

.article-document-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.4rem 0;
  color: #4b5563;
  font-size: 0.8rem;
}

.article-document-list span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.article-document-list--pending li {
  color: #6b7280;
}

.article-radio-group {
  display: grid;
  gap: 0.65rem;
}

.article-radio-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  cursor: pointer;
}

.article-modal-footer {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.article-footer-options,
.article-footer-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
}

.article-status-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.95rem;
  font-weight: 600;
}

.article-save-button,
.article-secondary-button {
  min-width: 7.5rem;
  min-height: 2.15rem;
  padding: 0.35rem 0.95rem;
  color: #fff;
  background: #358c4e;
  border: 1px solid #358c4e;
  border-radius: 0.375rem;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.2;
}

.article-secondary-button {
  color: #374151;
  background: #fff;
  border-color: #d1d5db;
}

.article-save-button:hover {
  background: #2f7d46;
  border-color: #2f7d46;
}

.article-secondary-button:hover {
  background: #f9fafb;
}

.article-secondary-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.article-cropper-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(75, 85, 99, 0.75);
}

.article-cropper-card {
  width: calc(100vw - 2rem);
  max-width: 560px;
  padding: 1.25rem;
  overflow: hidden;
  background: #f3f4f6;
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
}

.article-cropper-image {
  overflow: hidden;
}

.article-cropper-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
  margin-top: 1rem;
}

.article-suggestions-overlay {
  position: fixed;
  inset: 0;
  z-index: 55;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(75, 85, 99, 0.62);
}

.article-suggestions-card {
  width: calc(100vw - 2rem);
  max-width: 820px;
  padding: 1.25rem;
  background: #fff;
  border: 1px solid #dbe1ea;
  border-radius: 0.5rem;
  box-shadow: 0 24px 60px rgba(17, 24, 39, 0.24);
}

.article-suggestions-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.article-suggestions-header .article-panel-title {
  margin-bottom: 0.5rem;
}

.article-suggestions-header-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.article-suggestions-close,
.article-suggestions-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  color: #9ca3af;
  background: transparent;
  border: 0;
  border-radius: 999px;
}

.article-suggestions-refresh {
  color: #4b5563;
}

.article-suggestions-close:hover,
.article-suggestions-refresh:hover:not(:disabled) {
  color: #111827;
  background: #f3f4f6;
}

.article-suggestions-refresh:disabled {
  cursor: wait;
  color: #9ca3af;
}

.image-suggestions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.image-suggestions__footer {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.image-suggestion {
  position: relative;
  display: block;
  min-width: 0;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  text-align: left;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
}

.image-suggestion--selected {
  border-color: #2563eb;
}

.image-suggestion img {
  display: block;
  width: 100%;
  aspect-ratio: 595 / 350;
  object-fit: cover;
  background: #f3f4f6;
}

@media (max-width: 640px) {
  .image-suggestions {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .article-modal-shell {
    max-height: 90vh;
    padding: 2.5rem 1rem 1rem;
  }

  .article-modal-header h3 {
    margin-bottom: 1.5rem;
  }

  .article-modal-layout {
    grid-template-columns: 1fr;
  }

  .article-modal-sidebar {
    padding: 1.4rem;
  }

  .article-sidebar-section + .article-sidebar-section {
    margin-top: 2rem;
  }

  .article-illustration-actions,
  .article-modal-footer {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .article-footer-options,
  .article-footer-actions {
    width: 100%;
  }

  .article-footer-actions {
    justify-content: flex-end;
  }
}
</style>
